import { useInjection } from 'inversify-react';
import { observer } from 'mobx-react-lite';
import { ReactElement, useContext, useEffect, useState } from 'react';
import { useLoader } from '../../../__legacy__/helpers/useLoader';
import { AdminSearch } from '../../../__legacy__/modules/admin/components/AdminSearch';
import { IWorkspaceService } from '../../../__legacy__/modules/workspaces/workspaces.service';
import { S_TabsCard } from '../../../design/card/tabsCard/S_TabsCards';
import { ColumnLayout } from '../../common/layouts/columnLayout/ColumnLayout';
import { IOverlayLoaderStore } from '../../common/OverlayLoader.store';
import { Settings } from '../../settings/SettingsContext';
import { ICurrentWorkspaceStore } from '../../workspaces/currentWorkspace/CurrentWorkspace.store';
import { IAdminService } from '../admin.service';
import { IWorkspace } from '../admin.store';
import { AdminTabs } from '../components/AdminTabs';
import { AdminWorkspacesList } from '../components/AdminWorkspacesList';
import { ChangeWorkspaceCostLimitModal } from '../components/ChangeWorkspaceCostLimitModal';
import { ResetWorkspaceModal } from '../components/ResetWorkspaceModal';

interface IProps {
  search: string;
}

export const AdminWorkspacesListContainer = observer((props: IProps): ReactElement => {
  const adminService = useInjection(IAdminService.$);
  const currentWorkspaceStore = useInjection(ICurrentWorkspaceStore.$);
  const workspaceService = useInjection(IWorkspaceService.$);
  const overlayLoader = useInjection(IOverlayLoaderStore.$);

  const settingsContext = useContext(Settings);
  const [showChangeLimitModal, setShowChangeLimitModal] = useState<boolean>(false);
  const [showResetWorkspaceModal, setShowResetWorkspaceModal] = useState<boolean>(false);
  const [chosenWorkspace, setChosenWorkspace] = useState<IWorkspace | null>(null);
  const { withLoaderAsync } = useLoader();

  useEffect(() => {
    adminService.initPageSize(settingsContext.store.adminWorkspacesPageSize);
    void refreshAsync();
  }, []);

  const refreshAsync = async (): Promise<void> => {
    const workspaceId = currentWorkspaceStore.currentWorkspace.id;

    if (!workspaceId) {
      return;
    }

    await adminService.refreshAsync(props.search);
  };

  const handlePaginationChange = async (pageNumber: number, pageSize: number): Promise<void> => {
    await adminService.changePagination(pageNumber, pageSize, props.search);
  };

  const handleOrderChange = async (orderBy: string, orderType: string): Promise<void> => {
    await adminService.changeOrder(orderBy, orderType, props.search);
  };

  const handleChange = async (pageNumber: number, pageSize: number): Promise<void> => {
    settingsContext.setAdminWorkspacesPageSize(pageSize);
    await handlePaginationChange(pageNumber, pageSize);
  };

  const handleModalChangeWorkspaceLimit = (workspace: IWorkspace): void => {
    setShowChangeLimitModal(true);
    setChosenWorkspace(workspace);
  };

  const handleChangeWorkspaceLimitConfirmed = async (): Promise<void> => {
    if (chosenWorkspace) {
      await adminService.changeWorkspaceLimitAsync(chosenWorkspace);
      handleModalChangeWorkspaceLimitCanceled();
    }
  };

  const handleModalChangeWorkspaceLimitCanceled = (): void => {
    setShowChangeLimitModal(false);
    setChosenWorkspace(null);
  };

  const handleChangeWorkspaceLimit = (value: string): void => {
    if (chosenWorkspace) {
      const updated = { ...chosenWorkspace, cloudCostsLimit: value === '' ? 0 : parseInt(value, 10) };
      setChosenWorkspace(updated);
    }
  };

  const handleModalResetWorkspace = (workspace: IWorkspace): void => {
    setShowResetWorkspaceModal(true);
    setChosenWorkspace(workspace);
  };

  const handleModalResetWorkspaceCanceled = (): void => {
    setShowResetWorkspaceModal(false);
    setChosenWorkspace(null);
  };

  const handleModalResetWorkspaceConfirmed = async (): Promise<void> => {
    if (chosenWorkspace) {
      await withLoaderAsync(async (): Promise<void> => {
        await workspaceService.resetUserWorkspaceAsync(chosenWorkspace.id);
      }, 'workspace-reset');
    }

    setShowResetWorkspaceModal(false);
    setChosenWorkspace(null);
    await refreshAsync();
  };

  const handleSearchChange = async (search: string): Promise<void> => {
    await adminService.workspacesListSearchChange(search);
  };

  const { workspaces, workspacesPaging } = adminService.store;
  const { pageNumber, totalCount, orderType, orderBy, pageSize } = workspacesPaging;

  return (
    <ColumnLayout size='none'>
      <AdminTabs />
      <S_TabsCard>
        <ColumnLayout size='sm'>
          <AdminSearch onChange={handleSearchChange} />
          <AdminWorkspacesList
            isLoading={overlayLoader.isSpinnerVisible('workspaces-list-table')}
            workspaces={workspaces}
            pagination={{
              pageNumber,
              totalCount,
              pageSize: pageSize,
              onChange: handleChange,
            }}
            sorting={{
              orderBy: orderBy || '',
              orderType: orderType || '',
            }}
            onSortingChange={handleOrderChange}
            onChangeWorkspaceCostLimit={handleModalChangeWorkspaceLimit}
            onResetWorkspace={handleModalResetWorkspace}
          />
          <ChangeWorkspaceCostLimitModal
            isOpened={showChangeLimitModal}
            workspace={chosenWorkspace}
            onChange={handleChangeWorkspaceLimit}
            onCanceled={handleModalChangeWorkspaceLimitCanceled}
            onConfirmed={handleChangeWorkspaceLimitConfirmed}
            showSpinner={overlayLoader.isSpinnerVisible('change-workspace-limit')}
          />
          <ResetWorkspaceModal
            isOpened={showResetWorkspaceModal}
            onCanceled={handleModalResetWorkspaceCanceled}
            onConfirmed={handleModalResetWorkspaceConfirmed}
          />
        </ColumnLayout>
      </S_TabsCard>
    </ColumnLayout>
  );
});
